// src/pages/ResetPassword.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Alert,
  Box,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Grid,
  Fade,
} from '@mui/material';
import {
  PersonAdd,
  LockReset,
  Visibility,
  VisibilityOff,
  School,
  VerifiedUser,
  Search,
  Block,
  CheckCircle,
} from '@mui/icons-material';
import { getRoleFromToken } from '../utils/auth';
import { useNavigate } from 'react-router-dom';
import api from '../api';

function ResetPassword() {
  const navigate = useNavigate();
  const role = getRoleFromToken ? getRoleFromToken() : null;
  // Only superadmin should access user management
  useEffect(() => {
    if (role !== 'superadmin') {
      navigate('/dashboard');
    }
  }, [role, navigate]);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openAddUser, setOpenAddUser] = useState(false);
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Add user form
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    role: 'teacher',
  });

  // Reset password form
  const [resetPassword, setResetPassword] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setError('');
      setSuccess('');
      const response = await api.get('/api/users/list');
      setUsers(response.data);
    } catch {
      setError('Failed to load users');
    }
  };

  const handleAddUser = async () => {
    try {
      setError('');
      setSuccess('');
      
      if (!newUser.username || !newUser.password) {
        setError('Username and password are required');
        return;
      }

      await api.post('/api/users/create', newUser);
      setSuccess(`User ${newUser.username} created successfully`);
      setOpenAddUser(false);
      setNewUser({ username: '', password: '', role: 'teacher' });
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create user');
    }
  };

  const handleResetPassword = async () => {
    try {
      setError('');
      setSuccess('');
      
      if (!resetPassword) {
        setError('Password is required');
        return;
      }

      await api.put(`/api/users/reset-password/${selectedUser._id}`, {
        newPassword: resetPassword,
      });
      setSuccess(`Password reset successfully for ${selectedUser.username}`);
      setOpenResetDialog(false);
      setSelectedUser(null);
      setResetPassword('');
    } catch {
      setError('Failed to reset password');
    }
  };

  const openResetModal = (user) => {
    setSelectedUser(user);
    setResetPassword('');
    setOpenResetDialog(true);
  };

  const handleToggleDisable = async (userId, currentStatus) => {
    try {
      setError('');
      setSuccess('');
      await api.put(`/api/users/toggle-disable/${userId}`, {
        disabled: !currentStatus,
      });
      setSuccess(`User ${currentStatus ? 'enabled' : 'disabled'} successfully`);
      fetchUsers();
    } catch {
      setError('Failed to update user status');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  // Filter users based on search query
  const filteredUsers = users.filter((user) =>
    user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.role.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box sx={{ minHeight: '100vh', background: '#f5f7fa', py: 4 }}>
      <Container maxWidth="lg">
        <Fade in={true} timeout={800}>
          <Box>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 4,
                pb: 3,
                borderBottom: '3px solid',
                borderImage: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%) 1',
              }}
            >
              <Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: '#2c3e50',
                    mb: 0.5,
                    fontFamily: '"Poppins", "Inter", sans-serif',
                  }}
                >
                  User Management
                </Typography>
                <Typography variant="body2" sx={{ color: '#7f8c8d' }}>
                  Create users and manage password resets
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<PersonAdd />}
                  onClick={() => setOpenAddUser(true)}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 2,
                    px: 3,
                    py: 1.2,
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                    fontFamily: '"Inter", sans-serif',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Add User
                </Button>
                <Button
                  variant="outlined"
                  onClick={logout}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.2,
                    fontWeight: 600,
                    borderWidth: 2,
                    borderColor: '#dc3545',
                    color: '#dc3545',
                    fontFamily: '"Inter", sans-serif',
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: '#c82333',
                      background: '#fff5f5',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Logout
                </Button>
              </Box>
            </Box>

            {error && (
              <Alert
                severity="error"
                sx={{ mb: 3, borderRadius: 2 }}
                onClose={() => setError('')}
              >
                {error}
              </Alert>
            )}
            {success && (
              <Alert
                severity="success"
                sx={{ mb: 3, borderRadius: 2 }}
                onClose={() => setSuccess('')}
              >
                {success}
              </Alert>
            )}

            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <School sx={{ fontSize: 40 }} />
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Teachers
                        </Typography>
                        <Typography variant="h3" sx={{ fontWeight: 700 }}>
                          {users.filter((u) => u.role === 'teacher').length}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    color: 'white',
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(240, 147, 251, 0.3)',
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <VerifiedUser sx={{ fontSize: 40 }} />
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          QA Officers
                        </Typography>
                        <Typography variant="h3" sx={{ fontWeight: 700 }}>
                          {users.filter((u) => u.role === 'qa').length}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    color: 'white',
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(79, 172, 254, 0.3)',
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <LockReset sx={{ fontSize: 40 }} />
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Total Users
                        </Typography>
                        <Typography variant="h3" sx={{ fontWeight: 700 }}>
                          {users.length}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Users Table */}
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ p: 3, borderBottom: '1px solid #e8eaf0' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        color: '#2c3e50',
                        fontFamily: '"Poppins", "Inter", sans-serif',
                      }}
                    >
                      All Users
                    </Typography>
                    <TextField
                      placeholder="Search users..."
                      size="small"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search sx={{ color: '#7f8c8d' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        width: 300,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          fontFamily: '"Inter", sans-serif',
                        },
                      }}
                    />
                  </Box>
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ background: '#f8f9fa' }}>
                        <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>
                          Username
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>
                          Role
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }} align="center">
                          Status
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 600, color: '#2c3e50' }}
                          align="center"
                        >
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredUsers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                            <Typography sx={{ color: '#7f8c8d', fontFamily: '"Inter", sans-serif' }}>
                              {searchQuery ? 'No users found matching your search' : 'No users available'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredUsers.map((user) => (
                        <TableRow
                          key={user._id}
                          sx={{
                            '&:hover': { background: '#f8f9fa' },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <TableCell>
                            <Typography
                              sx={{ fontWeight: 500, fontFamily: '"Inter", sans-serif' }}
                            >
                              {user.username}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.role.toUpperCase()}
                              size="small"
                              sx={{
                                background:
                                  user.role === 'teacher'
                                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                    : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                color: 'white',
                                fontWeight: 600,
                                fontFamily: '"Inter", sans-serif',
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={user.disabled ? 'Disabled' : 'Active'}
                              size="small"
                              icon={user.disabled ? <Block sx={{ fontSize: 16 }} /> : <CheckCircle sx={{ fontSize: 16 }} />}
                              sx={{
                                background: user.disabled
                                  ? 'linear-gradient(135deg, #ff6b6b 0%, #c92a2a 100%)'
                                  : 'linear-gradient(135deg, #51cf66 0%, #37b24d 100%)',
                                color: 'white',
                                fontWeight: 600,
                                fontFamily: '"Inter", sans-serif',
                                '& .MuiChip-icon': {
                                  color: 'white',
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<LockReset />}
                                onClick={() => openResetModal(user)}
                                disabled={user.disabled}
                                sx={{
                                  borderRadius: 2,
                                  fontWeight: 600,
                                  fontFamily: '"Inter", sans-serif',
                                  borderColor: '#667eea',
                                  color: '#667eea',
                                  '&:hover': {
                                    borderColor: '#5568d3',
                                    background: 'rgba(102, 126, 234, 0.1)',
                                  },
                                  '&.Mui-disabled': {
                                    borderColor: '#e0e0e0',
                                    color: '#9e9e9e',
                                  },
                                }}
                              >
                                Reset
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={user.disabled ? <CheckCircle /> : <Block />}
                                onClick={() => handleToggleDisable(user._id, user.disabled)}
                                sx={{
                                  borderRadius: 2,
                                  fontWeight: 600,
                                  fontFamily: '"Inter", sans-serif',
                                  borderColor: user.disabled ? '#51cf66' : '#ff6b6b',
                                  color: user.disabled ? '#51cf66' : '#ff6b6b',
                                  '&:hover': {
                                    borderColor: user.disabled ? '#37b24d' : '#c92a2a',
                                    background: user.disabled ? 'rgba(81, 207, 102, 0.1)' : 'rgba(255, 107, 107, 0.1)',
                                  },
                                }}
                              >
                                {user.disabled ? 'Enable' : 'Disable'}
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Box>
        </Fade>

        {/* Add User Dialog */}
        <Dialog
          open={openAddUser}
          onClose={() => setOpenAddUser(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              fontWeight: 700,
              fontFamily: '"Poppins", "Inter", sans-serif',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonAdd />
              Add New User
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 3 }}>
            <TextField
              label="Username"
              fullWidth
              margin="normal"
              value={newUser.username}
              onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontFamily: '"Inter", sans-serif',
                },
              }}
            />
            <TextField
              label="Initial Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              margin="normal"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontFamily: '"Inter", sans-serif',
                },
              }}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select
                value={newUser.role}
                label="Role"
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                sx={{
                  borderRadius: 2,
                  fontFamily: '"Inter", sans-serif',
                }}
              >
                <MenuItem value="teacher">Teacher</MenuItem>
                <MenuItem value="qa">QA Officer</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button
              onClick={() => setOpenAddUser(false)}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                fontFamily: '"Inter", sans-serif',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddUser}
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 2,
                fontWeight: 600,
                px: 3,
                fontFamily: '"Inter", sans-serif',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                },
              }}
            >
              Create User
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog
          open={openResetDialog}
          onClose={() => setOpenResetDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              fontWeight: 700,
              fontFamily: '"Poppins", "Inter", sans-serif',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LockReset />
              Reset Password
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 3 }}>
            <Typography variant="body1" sx={{ mb: 3, color: '#4a5568' }}>
              Reset password for user:{' '}
              <strong>{selectedUser?.username}</strong>
            </Typography>
            <TextField
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              fullWidth
              margin="normal"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontFamily: '"Inter", sans-serif',
                },
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button
              onClick={() => setOpenResetDialog(false)}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                fontFamily: '"Inter", sans-serif',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 2,
                fontWeight: 600,
                px: 3,
                fontFamily: '"Inter", sans-serif',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                },
              }}
            >
              Reset Password
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}

export default ResetPassword;
